import { Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonChecklist } from 'components/common/checklist';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DetectionIssueType } from 'lib_ts/enums/session-hits.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IDetectionIssue } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { startCase } from 'lodash';
import { useState } from 'react';
import { useIntercom } from 'react-use-intercom';

const COMPONENT_NAME = 'DetectionIssueDialog';

// todo: TRANSLATIONS once copy is finalized

interface IProps {
  hitID: string;
  // provided when editing an existing issue
  defaultDetectionIssue?: Nullable<IDetectionIssue>;
  onClose: () => void;
  onSave: (hitID: string, detectionIssue: Partial<IDetectionIssue>) => void;
}

const DETECTION_ISSUE_LABEL_MAP: Record<DetectionIssueType, string> = {
  [DetectionIssueType.Speed]: 'Pitch Speed',
  [DetectionIssueType.VerticalBreak]: 'Pitch Vertical Break',
  [DetectionIssueType.HorizontalBreak]: 'Pitch Horizontal Break',
  [DetectionIssueType.HitResult]: 'Hit Result',
  [DetectionIssueType.HitMetrics]:
    'Hit Metrics - (LA, EV, Direction, Distance)',
  [DetectionIssueType.PlateLocation]: 'Plate Location',
  [DetectionIssueType.VideoQuality]: 'Video Compression/Quality',
};

const ISSUES = Object.values(DetectionIssueType).map((value) => ({
  label: DETECTION_ISSUE_LABEL_MAP[value] || startCase(value),
  value,
}));

export const DetectionIssueDialog = (props: IProps) => {
  const { hitID, defaultDetectionIssue, onClose } = props;

  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<DetectionIssueType[]>(
    defaultDetectionIssue?.issues || []
  );
  const [notes, setNotes] = useState<string>(
    defaultDetectionIssue?.notes || ''
  );

  const { show: openIntercom } = useIntercom();

  const onSubmit = async () => {
    try {
      if (!notes.trim()) {
        NotifyHelper.error({
          message_md:
            'Please provide some additional details about what looks incorrect',
        });
        return;
      }

      setLoading(true);

      await props.onSave(hitID, {
        issues,
        notes,
      });

      NotifyHelper.success({
        message_md: t('hitters.issue-reported-success-toast'),
      });

      onClose();
    } catch (error) {
      NotifyHelper.error({
        message_md: t('hitters.issue-reported-error-toast'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.SM}
        title={t('hitters.report-vision-detection-issues')}
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Text
              size="2"
              weight="regular"
              color={RADIX.COLOR.SECONDARY}
              mt="-1"
            >
              Use this form to flag <strong>Vision detection errors</strong>,
              where the system's read (speed, break, or outcome) does not match
              what actually occurred.
              <br />
              <br />
              For issues with pitches not looking right,{' '}
              <Text
                className="cursor-pointer"
                style={{
                  textDecoration: 'underline',
                }}
                color={RADIX.COLOR.ACCENT}
                onClick={(e) => {
                  e.preventDefault();
                  openIntercom();
                }}
              >
                please contact us.
              </Text>
            </Text>
            <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
              <Text size="3" weight="bold">
                {t('hitters.what-was-incorrectly-detected')}
              </Text>
              <CommonChecklist
                id="detection-issue-types"
                name="types"
                values={issues}
                disabled={loading}
                options={ISSUES}
                onChange={(selected) =>
                  setIssues(selected as DetectionIssueType[])
                }
              />
            </Flex>
            <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
              <Flex direction="column" gap={RADIX.FLEX.GAP.XS}>
                <Text size="2" weight="medium">
                  Details
                </Text>
                <Text color={RADIX.COLOR.HINT} size={RADIX.TEXT.SIZE.HINT}>
                  What is specifically off about these metrics?
                  <br />
                  e.g. Result says Fly Out instead of Ground Out
                </Text>
              </Flex>
              <CommonTextareaInput
                id="additional-notes"
                value={notes}
                disabled={loading}
                name="notes"
                onChange={(v) => setNotes(v || '')}
                placeholder={t(
                  'hitters.additional-notes-placeholder'
                ).toString()}
                rows={5}
              />
            </Flex>
          </Flex>
        }
        loading={loading}
        leftFooter={
          <Flex align="center" height="100%">
            {defaultDetectionIssue
              ? `Last sent on ${format(new Date(defaultDetectionIssue.date), 'EEE, MMM d h:mma')}`
              : undefined}
          </Flex>
        }
        buttons={[
          {
            label: t('common.cancel').toString(),
            color: RADIX.COLOR.NEUTRAL,
            variant: 'soft',
            loading: loading,
            onClick: () => onClose(),
          },
          {
            label: t('hitters.submit-report').toString(),
            color: RADIX.COLOR.ACCENT,
            variant: 'solid',
            loading: loading,
            onClick: () => onSubmit(),
          },
        ]}
        onClose={onClose}
      />
    </ErrorBoundary>
  );
};
